import React, { useState } from 'react';
import { MealItem, adminAPI } from '../api';
import MealForm from './MealForm';
import { formatTime, formatDisplayDate } from '../utils/dateUtils';

interface DayDetailProps {
  userId: number;
  date: string;
  items: MealItem[];
  isLoading: boolean;
  onClose: () => void;
  onMealUpdated: () => void;
}

const DayDetail: React.FC<DayDetailProps> = ({
  userId,
  date,
  items,
  isLoading,
  onClose,
  onMealUpdated,
}) => {
  const [editingItem, setEditingItem] = useState<MealItem | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDelete = async (mealId: number) => {
    if (!window.confirm('Are you sure you want to delete this meal item?')) {
      return;
    }

    setDeletingId(mealId);
    try {
      const response = await adminAPI.deleteMeal(mealId);
      if (response.success) {
        onMealUpdated();
      } else {
        alert(response.error || 'Failed to delete meal');
      }
    } catch (err) {
      alert('Failed to delete meal');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveEdit = async (item: MealItem | Omit<MealItem, 'id'>) => {
    if (!('id' in item)) return;
    try {
      const response = await adminAPI.updateMeal(item.id, {
        food_name: item.food_name,
        qty: item.qty,
        unit: item.unit,
        notes: item.notes,
        carbs: item.carbs,
        protein: item.protein,
        fat: item.fat,
        calories: item.calories,
      });
      if (response.success) {
        setEditingItem(null);
        onMealUpdated();
      } else {
        alert(response.error || 'Failed to update meal');
      }
    } catch (err) {
      alert('Failed to update meal');
    }
  };

  const handleSaveNew = async (mealData: MealItem | Omit<MealItem, 'id'>) => {
    try {
      const response = await adminAPI.createMeal(userId, {
        food_name: mealData.food_name,
        qty: mealData.qty,
        unit: mealData.unit,
        notes: mealData.notes,
        carbs: mealData.carbs,
        protein: mealData.protein,
        fat: mealData.fat,
        calories: mealData.calories,
        meal_time: mealData.meal_time,
      });
      if (response.success) {
        setIsAdding(false);
        onMealUpdated();
      } else {
        alert(response.error || 'Failed to create meal');
      }
    } catch (err) {
      alert('Failed to create meal');
    }
  };

  // Calculate totals
  const totals = items.reduce(
    (acc, item) => ({
      carbs: acc.carbs + item.carbs,
      protein: acc.protein + item.protein,
      fat: acc.fat + item.fat,
      calories: acc.calories + item.calories,
    }),
    { carbs: 0, protein: 0, fat: 0, calories: 0 }
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {formatDisplayDate(date)}
            </h2>
            <p className="text-sm text-gray-500">User ID: {userId}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Totals Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.round(totals.carbs)}g
                  </div>
                  <div className="text-xs text-blue-600">Carbs</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {Math.round(totals.protein)}g
                  </div>
                  <div className="text-xs text-green-600">Protein</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-600">
                    {Math.round(totals.fat)}g
                  </div>
                  <div className="text-xs text-amber-600">Fat</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {Math.round(totals.calories)}
                  </div>
                  <div className="text-xs text-purple-600">Calories</div>
                </div>
              </div>

              {/* Add Button */}
              <div className="mb-4">
                <button
                  onClick={() => setIsAdding(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  + Add Meal Item
                </button>
              </div>

              {/* Add Form */}
              {isAdding && (
                <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="text-sm font-medium text-green-800 mb-3">Add New Meal Item</h3>
                  <MealForm
                    defaultDate={date}
                    onSave={handleSaveNew}
                    onCancel={() => setIsAdding(false)}
                  />
                </div>
              )}

              {/* Meal Items */}
              {items.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No meals recorded for this day
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      {editingItem?.id === item.id ? (
                        <MealForm
                          item={editingItem}
                          onSave={handleSaveEdit}
                          onCancel={() => setEditingItem(null)}
                        />
                      ) : (
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {item.food_name}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {item.qty} {item.unit} &bull; {formatTime(item.meal_time)}
                              </p>
                              {item.notes && (
                                <p className="text-sm text-gray-400 mt-1 italic">
                                  {item.notes}
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setEditingItem(item)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                disabled={deletingId === item.id}
                                className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                              >
                                {deletingId === item.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                          <div className="flex space-x-4 text-sm">
                            <span className="text-blue-600">
                              {Math.round(item.carbs)}g carbs
                            </span>
                            <span className="text-green-600">
                              {Math.round(item.protein)}g protein
                            </span>
                            <span className="text-amber-600">
                              {Math.round(item.fat)}g fat
                            </span>
                            <span className="text-purple-600 font-medium">
                              {Math.round(item.calories)} cal
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayDetail;
